import AppBox from "../../../../commons/components/Box";
import {
  AppAvatarLabelSkeleton,
  AppButton,
  AppDivider,
  AppModal,
  AppPaper,
  AppText,
  AppTitle,
} from "../../../../commons/components";
import { Fragment, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks/app";
import { StatusEnum } from "../../../../commons/enums/status-enum";
import IconPersonAdd from "../../../../commons/components/icons/person-add";
import { RolePolicyActionEnum } from "../../../../commons/enums/role-policy-action.enum";
import ability, { Can } from "../../../../casl/Can";
import UserSearch from "../../users/components/UserSearch";
import { User } from "../../../../commons/models/user";
import ConfirmationModal from "../../../../commons/components/ConfirmationModal";
import { FootballTournamentManager } from "../../../../commons/models/football/football-tournament-manager";
import { useSnackbar } from "../../../../redux/snackbarProvider";
import AppBorderBox from "../../../../commons/components/BorderBox";
import { ResponseStatusCode } from "../../../../commons/enums/response-status-code.enum";
import TournamentManagerCard from "../../tournaments/components/tournamentManager/TournamentManagerCard";
import {
  addRemoveHostedTournamentManager,
  fetchHostedTournamentManager,
} from "../../../../redux/slices/football/admin/team/hosted-competition/manage-team-hosted-competition-manager";
import { ManageFootballTeam } from "../../../../commons/models/football/admin/manage-football-team";

export default function HostedTournamentManager() {
  const dispatch = useAppDispatch();
  const snackBar = useSnackbar();

  const {
    hostedTournamentManagers,
    hostedTournamentManagerFetchStatus,
    addhostedTournamentManagerStatus,
    removehostedTournamentManagerStatus,
  } = useAppSelector((state) => state.footballHostedCompetitionManagers);
  const { hostedCompetitionInfo } = useAppSelector((state) => state.footballManageHostedCompetitionDetail);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState<User | FootballTournamentManager>();
  const [confirmationDialogMode, setConfirmationDialogMode] = useState<"add" | "remove" | undefined>();

  useEffect(() => {
    if (hostedCompetitionInfo.id) {
      dispatch(
        fetchHostedTournamentManager({
          teamId: hostedCompetitionInfo.hostTeam.id,
          hostedTournamentId: hostedCompetitionInfo.id,
        })
      );
    }
  }, [hostedCompetitionInfo]);

  const handleManagerSelect = (users: User[]) => {
    setIsAddDialogOpen(false);
    handleSelectedManagerAndOpenConfirmationDialog(users[0], "add");
  };
  const handleConfirmationAction = () => {
    if (selectedManager) {
      dispatch(
        addRemoveHostedTournamentManager({
          teamId: hostedCompetitionInfo.hostTeam.id,
          hostedTournamentId: hostedCompetitionInfo.id,
          userId: selectedManager.id,
          remove: confirmationDialogMode === "remove",
        })
      )
        .unwrap()
        .then(() => {
          setSelectedManager(undefined);
          dispatch(
            fetchHostedTournamentManager({
              teamId: hostedCompetitionInfo.hostTeam.id,
              hostedTournamentId: hostedCompetitionInfo.id,
            })
          );
          const action = confirmationDialogMode === "remove" ? "Removed" : "Added";
          snackBar({
            key: `hosted-tournament-manager-${confirmationDialogMode}`,
            text: `Manager ${action} Successfully`,
            variant: "success",
          });
        })
        .catch((err) => {
          if (err.status !== ResponseStatusCode.EXCEPTION_FAILED) {
            snackBar({
              key: err.response.data.code,
              text: err.response.data.message,
              variant: "danger",
            });
          }
        });
      setConfirmationDialogMode(undefined);
    }
  };

  const handleSelectedManagerAndOpenConfirmationDialog = (
    user: User | FootballTournamentManager,
    dialogMode: "add" | "remove"
  ) => {
    setSelectedManager(user);
    setConfirmationDialogMode(dialogMode);
  };

  const getConfirmationMessage = () => {
    if (!selectedManager) return "";
    const managerName = `<strong>${selectedManager.fullName}</strong>`;
    const competitionName = `<strong>${hostedCompetitionInfo.title}</strong>`;
    return confirmationDialogMode === "add"
      ? `Do you want to proceed with appointing ${managerName} as manager of ${competitionName}?`
      : `Do you want to proceed with removing ${managerName} as the manager of ${competitionName}?`;
  };
  return (
    <AppBox flexDirection="column" gap="xs">
      <AppBox justifyContent={"space-between"} alignItems={"center"}>
        <AppTitle as="h5">Manager</AppTitle>
      </AppBox>
      <AppPaper padding={"none"} shadow={"xs"}>
        {hostedTournamentManagerFetchStatus === StatusEnum.Loading ? (
          <AppBox flexDirection="column">
            <AppAvatarLabelSkeleton />
          </AppBox>
        ) : hostedTournamentManagers.length === 0 ? (
          <AppPaper padding="sm" shadow="xs">
            <AppBorderBox borderVariant="dashed">
              <AppBox flexDirection="column" alignItems="center" gap="sm" justifyContent="center" p="sm">
                <Can I={RolePolicyActionEnum.UPDATE} this={new ManageFootballTeam(hostedCompetitionInfo.hostTeam)}>
                  <IconPersonAdd width={40} height={40} color="rgba(var(--border-200))" />
                </Can>
                <AppText fontWeight="semibold">There are not any manager in the tournament.</AppText>
                <Can I={RolePolicyActionEnum.UPDATE} this={new ManageFootballTeam(hostedCompetitionInfo.hostTeam)}>
                  <AppButton size="sm" label="Add Manager" onClick={() => setIsAddDialogOpen(true)} />
                </Can>
              </AppBox>
            </AppBorderBox>
          </AppPaper>
        ) : (
          <AppBox flexDirection="column">
            {hostedTournamentManagers.mapArray((manager, index) => (
              <Fragment key={manager.id}>
                <TournamentManagerCard
                  manager={manager}
                  withRemove={ability.can(
                    RolePolicyActionEnum.UPDATE,
                    new ManageFootballTeam(hostedCompetitionInfo.hostTeam)
                  )}
                  onRemove={(user) => handleSelectedManagerAndOpenConfirmationDialog(user, "remove")}
                />
                {index !== hostedTournamentManagers.length - 1 && <AppDivider />}
              </Fragment>
            ))}
          </AppBox>
        )}
      </AppPaper>
      <AppModal
        opened={isAddDialogOpen}
        onClose={() => {
          setIsAddDialogOpen(false);
        }}
        titleIcon={<IconPersonAdd />}
        title={"Choose Tournament Manager"}
        withoutButtons
      >
        <UserSearch
          userSelectedUsers={hostedTournamentManagers?.all() as User[]}
          onSelectedUsers={handleManagerSelect}
          teamAvailableUsers
          team={new ManageFootballTeam(hostedCompetitionInfo.hostTeam)}
        />
      </AppModal>
      <ConfirmationModal
        isOpen={confirmationDialogMode !== undefined}
        title={confirmationDialogMode === "add" ? "Add Tournament Manager" : "Want To Remove This Manager?"}
        message={getConfirmationMessage()}
        onCancel={() => setConfirmationDialogMode(undefined)}
        onConfirm={handleConfirmationAction}
        withLoadingButton={
          removehostedTournamentManagerStatus === StatusEnum.Loading ||
          addhostedTournamentManagerStatus === StatusEnum.Loading
        }
      />
    </AppBox>
  );
}
